export interface PaymentAccount {
  id: string;
  name: string;
  description?: string;
  type: PaymentAccountType;
  bank_name?: string;
  account_number?: string;
  agency?: string;
  initial_balance: number;
  current_balance: number;
  balance: number;
  status: PaymentAccountStatus;
  color?: string;
  user_id: string;
  created_at: Date;
  updated_at: Date;
  is_active: boolean;
  is_default: boolean;
  transaction_count?: number;
  logo_url?: string;
}

export enum PaymentAccountType {
  CHECKING = 'checking',
  SAVINGS = 'savings',
  CREDIT_CARD = 'credit_card',
  INVESTMENT = 'investment',
  OTHER = 'other'
}

export enum PaymentAccountStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLOCKED = 'blocked'
}

export interface PaymentAccountFormData {
  name: string;
  description?: string;
  type: PaymentAccountType;
  bank_name?: string;
  account_number?: string;
  agency?: string;
  initial_balance: number;
  status: PaymentAccountStatus;
  color?: string;
  is_default: boolean;
}

export interface PaymentAccountFilters {
  search?: string;
  type?: PaymentAccountType;
  status?: PaymentAccountStatus;
  bank_name?: string;
  sortBy?: 'name' | 'type' | 'current_balance' | 'created_at' | 'transaction_count';
  sortOrder?: 'asc' | 'desc';
}

export interface PaymentAccountSummary {
  total: number;
  total_balance: number;
  by_type: {
    [key in PaymentAccountType]: {
      count: number;
      balance: number;
    };
  };
  by_status: {
    [key in PaymentAccountStatus]: number;
  };
  most_used: PaymentAccount[];
  highest_balance: PaymentAccount[];
}

// Utility functions
export const formatPaymentAccountType = (type: PaymentAccountType): string => {
  switch (type) {
    case PaymentAccountType.CHECKING:
      return 'Conta Corrente';
    case PaymentAccountType.SAVINGS:
      return 'Poupança';
    case PaymentAccountType.CREDIT_CARD:
      return 'Cartão de Crédito';
    case PaymentAccountType.INVESTMENT:
      return 'Investimento';
    case PaymentAccountType.OTHER:
      return 'Outros';
    default:
      return 'Desconhecido';
  }
};

export const formatPaymentAccountStatus = (status: PaymentAccountStatus): string => {
  switch (status) {
    case PaymentAccountStatus.ACTIVE:
      return 'Ativa';
    case PaymentAccountStatus.INACTIVE:
      return 'Inativa';
    case PaymentAccountStatus.BLOCKED:
      return 'Bloqueada';
    default:
      return 'Desconhecido';
  }
};

export const getPaymentAccountStatusColor = (status: PaymentAccountStatus): string => {
  switch (status) {
    case PaymentAccountStatus.ACTIVE:
      return 'text-green-600 bg-green-50 border-green-200';
    case PaymentAccountStatus.INACTIVE:
      return 'text-gray-600 bg-gray-50 border-gray-200';
    case PaymentAccountStatus.BLOCKED:
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getPaymentAccountTypeColor = (type: PaymentAccountType): string => {
  switch (type) {
    case PaymentAccountType.CHECKING:
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case PaymentAccountType.SAVINGS:
      return 'text-green-600 bg-green-50 border-green-200';
    case PaymentAccountType.CREDIT_CARD:
      return 'text-purple-600 bg-purple-50 border-purple-200';
    case PaymentAccountType.INVESTMENT:
      return 'text-indigo-600 bg-indigo-50 border-indigo-200';
    case PaymentAccountType.OTHER:
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(amount);
};

export const validateAccountNumber = (accountNumber: string, type: PaymentAccountType): boolean => {
  if (!accountNumber) return true; // Optional field

  switch (type) {
    case PaymentAccountType.CHECKING:
    case PaymentAccountType.SAVINGS:
      return accountNumber.length >= 5 && accountNumber.length <= 15;
    case PaymentAccountType.CREDIT_CARD:
      return accountNumber.replace(/\s/g, '').length >= 13;
    case PaymentAccountType.INVESTMENT:
    case PaymentAccountType.OTHER:
      return true; // More flexible validation for these types
    default:
      return true;
  }
};

// Mock data functions removed - now using real database data

export const buildPaymentAccountSummary = (accounts: PaymentAccount[]): PaymentAccountSummary => {
  const summary: PaymentAccountSummary = {
    total: accounts.length,
    total_balance: 0,
    by_type: {} as any,
    by_status: {
      [PaymentAccountStatus.ACTIVE]: 0,
      [PaymentAccountStatus.INACTIVE]: 0,
      [PaymentAccountStatus.BLOCKED]: 0
    },
    most_used: [],
    highest_balance: []
  };

  // Initialize by_type
  Object.values(PaymentAccountType).forEach(type => {
    summary.by_type[type] = { count: 0, balance: 0 };
  });

  accounts.forEach(account => {
    // Skip if account is null or undefined
    if (!account) return;
    
    summary.total_balance += account.current_balance || 0;
    
    // Safely handle undefined type and status
    if (account.type && summary.by_type[account.type]) {
      summary.by_type[account.type].count++;
      summary.by_type[account.type].balance += account.current_balance || 0;
    }
    
    if (account.status && summary.by_status[account.status] !== undefined) {
      summary.by_status[account.status]++;
    }
  });

  // Sort by usage and balance
  const sortedByUsage = [...accounts].sort((a, b) => (b.transaction_count || 0) - (a.transaction_count || 0));
  summary.most_used = sortedByUsage.slice(0, 3);

  const sortedByBalance = [...accounts].sort((a, b) => b.current_balance - a.current_balance);
  summary.highest_balance = sortedByBalance.slice(0, 3);

  return summary;
};
